// PresencasDashboard.jsx — Dashboard de registos de presenças (Ginástica Laboral)
// Uso no HUB:  <PresencasDashboard supabase={supabase} cliente="Amarsul" interno />
// Página do cliente: usado por PresencasPublica.jsx (obterDados vem da Edge Function presencas-portal)
//  - interno=true  → mostra professores e o botão "Atualizar do Jotform"
//  - interno=false → versão para cliente (sem professores e sem botão; as observações aparecem, como no relatório atual)
// Dados: RPC presencas_agregado(p_cliente, p_de, p_ate, p_local). Sem dependências de gráficos.

import React, { useEffect, useMemo, useState } from "react";

const C = {
  roxo: "#691FB1", roxoClaro: "#C6C3E7", roxoEsc: "#736CCE",
  rosa: "#FF357B", rosaClaro: "#FFC6D9", azul: "#57B1DD", azulClaro: "#E5F7FF", azulEsc: "#2C6EAF",
  texto: "#1F1A2E", cinza: "#6B6880", linha: "#ECEAF5", fundo: "#FAF9FD",
};
const COR_LOCAL = [C.roxo, C.azul, C.rosa, C.roxoEsc, C.azulEsc];
const COR_SERIE = [C.roxo, C.azul, C.rosa, C.roxoEsc, C.azulEsc, "#BE1356", "#9AD3EE", C.roxoClaro, C.rosaClaro, "#B8A9E8"];
const PILL_LOCAL = [[C.roxoClaro, C.roxo], [C.azulClaro, C.azulEsc], [C.rosaClaro, "#BE1356"]];
const pillLocal = (locais, l) => PILL_LOCAL[Math.max(0, (locais || []).indexOf(l)) % PILL_LOCAL.length];
const ESCALA = ["Muito motivado", "Motivado", "Neutro", "Desmotivado", "Muito desmotivado"];
const COR_MOTIV = { "Muito motivado": C.roxo, Motivado: C.roxoEsc, Neutro: C.roxoClaro, Desmotivado: C.rosaClaro, "Muito desmotivado": C.rosa };
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("pt-PT"));
const mesLbl = (m) => { const [a, mm] = m.split("-"); return `${MESES[+mm - 1]} ${a.slice(2)}`; };
const dataLbl = (d) => (d ? new Date(d).toLocaleDateString("pt-PT") : "—");
const hoje = () => new Date().toISOString().slice(0, 10);
const menosMeses = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); d.setDate(1); return d.toISOString().slice(0, 10); };

const st = {
  wrap: { fontFamily: "Helvetica, Arial, sans-serif", color: C.texto, background: C.fundo, padding: 20, borderRadius: 14 },
  head: { display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  h1: { margin: 0, fontSize: 20, color: C.roxo, fontWeight: 800 },
  sub: { fontSize: 12, color: C.cinza, marginTop: 2 },
  filtros: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  chip: (on) => ({ border: `1px solid ${on ? C.roxo : C.linha}`, background: on ? C.roxo : "#fff", color: on ? "#fff" : C.texto,
    borderRadius: 999, padding: "5px 12px", fontSize: 12, cursor: "pointer" }),
  input: { border: `1px solid ${C.linha}`, borderRadius: 8, padding: "5px 8px", fontSize: 12, background: "#fff" },
  btn: { border: "none", background: C.rosa, color: "#fff", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 14 },
  card: { background: "#fff", border: `1px solid ${C.linha}`, borderRadius: 12, padding: 16, marginBottom: 14 },
  kpiV: { fontSize: 26, fontWeight: 800, color: C.roxo, lineHeight: 1.1 },
  pill: (bg, cor) => ({ display: "inline-block", background: bg, color: cor, borderRadius: 999, padding: "2px 9px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }),
  kpiL: { fontSize: 12, color: C.cinza, marginTop: 4 },
  h2: { margin: "0 0 12px", fontSize: 14, fontWeight: 800, color: C.roxo },
  dois: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 },
  th: { textAlign: "left", fontSize: 11, color: "#fff", background: C.roxo, fontWeight: 700, padding: "8px 10px", letterSpacing: 0.3, textTransform: "uppercase" },
  td: { fontSize: 12, padding: "7px 10px", borderBottom: `1px solid ${C.linha}` },
  zebra: (i) => ({ background: i % 2 ? "#F6F2FC" : "#fff" }),
  tabela: { width: "100%", borderCollapse: "separate", borderSpacing: 0, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.roxoClaro}` },
  legenda: { display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, color: C.cinza, marginTop: 8 },
};

function Kpi({ v, l, cor }) {
  return (
    <div style={{ ...st.card, marginBottom: 0, borderTop: `4px solid ${cor || C.roxo}` }}>
      <div style={{ ...st.kpiV, color: cor || C.roxo }}>{v}</div>
      <div style={st.kpiL}>{l}</div>
    </div>
  );
}

function Legenda({ itens }) {
  return (
    <div style={st.legenda}>
      {itens.map(([nome, cor]) => (
        <span key={nome} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: cor }} />{nome}
        </span>
      ))}
    </div>
  );
}

// Barras empilhadas por mês (participações por local)
function EvolucaoMensal({ dados, locais, chave = "local" }) {
  const meses = [...new Set(dados.map((d) => d.mes))].sort();
  const val = (m, l) => dados.find((d) => d.mes === m && d[chave] === l)?.participacoes || 0;
  const tot = meses.map((m) => locais.reduce((s, l) => s + val(m, l), 0));
  const max = Math.max(1, ...tot);
  const [hover, setHover] = useState(null);
  if (!meses.length) return <div style={{ fontSize: 12, color: C.cinza }}>Sem dados no período.</div>;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 180, padding: "0 2px" }}>
        {meses.map((m, i) => (
          <div key={m} style={{ flex: 1, minWidth: 18, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "flex-end", position: "relative" }}
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <div style={{ fontSize: 10, color: C.cinza, marginBottom: 3 }}>{fmt(tot[i])}</div>
            <div style={{ width: "100%", maxWidth: 38, height: `${(tot[i] / max) * 150}px`, display: "flex", flexDirection: "column-reverse", borderRadius: "4px 4px 0 0", overflow: "hidden" }}>
              {locais.map((l, j) => (
                <div key={l} style={{ height: tot[i] ? `${(val(m, l) / tot[i]) * 100}%` : 0, background: COR_SERIE[j % COR_SERIE.length], borderTop: j ? "1px solid #fff" : "none" }} />
              ))}
            </div>
            {hover === i && (
              <div style={{ position: "absolute", bottom: "100%", background: C.texto, color: "#fff", fontSize: 11, padding: "6px 8px", borderRadius: 6, whiteSpace: "nowrap", zIndex: 2 }}>
                <b>{mesLbl(m)}</b>{locais.map((l) => <div key={l}>{l}: {fmt(val(m, l))}</div>)}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, borderTop: `1px solid ${C.linha}`, paddingTop: 4 }}>
        {meses.map((m) => <div key={m} style={{ flex: 1, minWidth: 18, textAlign: "center", fontSize: 10, color: C.cinza }}>{mesLbl(m)}</div>)}
      </div>
      <Legenda itens={locais.map((l, j) => [l, COR_SERIE[j % COR_SERIE.length]])} />
    </div>
  );
}

// Barra horizontal simples
function BarraH({ label, valor, max, cor, extra }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 70px", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 12 }}>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={label}>{label}</span>
      <div style={{ background: C.linha, borderRadius: 4, height: 12 }}>
        <div style={{ width: `${max ? (valor / max) * 100 : 0}%`, background: cor || C.roxo, height: "100%", borderRadius: 4 }} />
      </div>
      <span style={{ textAlign: "right", color: C.cinza }}>{extra ?? fmt(valor)}</span>
    </div>
  );
}

// Motivação por mês: distribuição (100% empilhado) + média na escala 1–5
function MotivacaoMensal({ dados, medias }) {
  const meses = [...new Set(dados.map((d) => d.mes))].sort();
  const cats = ESCALA.filter((c) => dados.some((d) => d.motivacao === c));
  const media = (m) => medias.find((x) => x.mes === m)?.media;
  if (!meses.length) return <div style={{ fontSize: 12, color: C.cinza }}>Sem registos de motivação.</div>;
  return (
    <div>
      {meses.map((m) => {
        const linhas = dados.filter((d) => d.mes === m);
        const tot = linhas.reduce((s, d) => s + d.aulas, 0);
        return (
          <div key={m} style={{ display: "grid", gridTemplateColumns: "56px 1fr 44px", gap: 8, alignItems: "center", marginBottom: 5, fontSize: 11 }}>
            <span style={{ color: C.cinza }}>{mesLbl(m)}</span>
            <div style={{ display: "flex", height: 12, borderRadius: 4, overflow: "hidden", background: C.linha }}>
              {cats.map((c) => {
                const v = linhas.find((d) => d.motivacao === c)?.aulas || 0;
                return v ? <div key={c} title={`${c}: ${v} aulas (${Math.round((v / tot) * 100)}%)`} style={{ width: `${(v / tot) * 100}%`, background: COR_MOTIV[c] }} /> : null;
              })}
            </div>
            <span style={{ textAlign: "right", fontWeight: 700, color: C.roxo }}>{media(m) != null ? Number(media(m)).toFixed(1).replace(".", ",") : "—"}</span>
          </div>
        );
      })}
      <Legenda itens={cats.map((c) => [c, COR_MOTIV[c]])} />
      <div style={{ fontSize: 10, color: C.cinza, marginTop: 4 }}>À direita: média do mês na escala 1 (muito desmotivado) a 5 (muito motivado).</div>
    </div>
  );
}

// Observações dos professores
function Observacoes({ itens, locais }) {
  const [n, setN] = useState(15);
  const [q, setQ] = useState("");
  const filtradas = itens.filter((o) => !q || `${o.nota} ${o.turno || ""} ${o.local || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <input style={{ ...st.input, width: "100%", maxWidth: 320, marginBottom: 10 }} placeholder="Pesquisar nas observações…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div style={{ overflowX: "auto" }}>
        <table style={{ ...st.tabela, minWidth: 560 }}>
          <thead><tr>
            <th style={{ ...st.th, width: 90 }}>Data</th><th style={{ ...st.th, width: 70 }}>Local</th>
            <th style={{ ...st.th, width: 170 }}>Turno</th><th style={{ ...st.th, width: 50, textAlign: "right" }}>Part.</th><th style={st.th}>Observação</th>
          </tr></thead>
          <tbody>
            {filtradas.slice(0, n).map((o, i) => (
              <tr key={i} style={st.zebra(i)}>
                <td style={{ ...st.td, fontWeight: 700, color: C.roxo }}>{dataLbl(o.data)}{o.estimada ? "*" : ""}</td>
                <td style={st.td}>{o.local ? <span style={st.pill(...pillLocal(locais, o.local))}>{o.local}</span> : "—"}</td>
                <td style={st.td}>{o.turno || "—"}</td>
                <td style={{ ...st.td, textAlign: "right" }}>{fmt(o.participantes)}</td>
                <td style={st.td}>{o.nota}</td>
              </tr>
            ))}
            {!filtradas.length && <tr><td style={st.td} colSpan={5}>Sem observações.</td></tr>}
          </tbody>
        </table>
      </div>
      {filtradas.length > n && (
        <button style={{ ...st.chip(false), marginTop: 10 }} onClick={() => setN(n + 30)}>Ver mais ({filtradas.length - n})</button>
      )}
    </div>
  );
}

export default function PresencasDashboard({ supabase, cliente = "Amarsul", interno = true, obterDados, titulo, aoSair }) {
  const [periodo, setPeriodo] = useState("12m");
  const [de, setDe] = useState(menosMeses(11));
  const [ate, setAte] = useState(hoje());
  const [local, setLocal] = useState(null);
  const [setor, setSetor] = useState(null);
  const [hora, setHora] = useState(null);
  const [agrupar, setAgrupar] = useState("local");
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  const [aCarregar, setACarregar] = useState(true);
  const [aSincronizar, setASincronizar] = useState(false);
  const [ordemSetor, setOrdemSetor] = useState("participacoes");

  const aplicarPeriodo = (p) => {
    setPeriodo(p);
    if (p === "3m") { setDe(menosMeses(2)); setAte(hoje()); }
    if (p === "12m") { setDe(menosMeses(11)); setAte(hoje()); }
    if (p === "ano") { setDe(`${new Date().getFullYear()}-01-01`); setAte(hoje()); }
    if (p === "tudo") { setDe(""); setAte(""); }
  };

  const carregar = async () => {
    setACarregar(true); setErro(null);
    const { data, error } = obterDados
      ? await obterDados({ de: de || null, ate: ate || null, local, setor, hora })
      : await supabase.rpc("presencas_agregado", { p_cliente: cliente, p_de: de || null, p_ate: ate || null, p_local: local, p_setor: setor, p_hora: hora });
    if (error) setErro(error.message); else setD(data);
    setACarregar(false);
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [cliente, de, ate, local, setor, hora]);

  const sincronizar = async () => {
    setASincronizar(true);
    const { data, error } = await supabase.functions.invoke("ingerir-presencas", { body: { cliente } });
    setASincronizar(false);
    if (error || !data?.ok) { setErro(error?.message || data?.erro || "Falha na sincronização"); return; }
    carregar();
  };

  const locais = useMemo(() => (d?.locais || []).slice().sort(), [d]);
  const opcTurnos = d?.opcoes?.turnos || [];
  const setoresOpc = useMemo(() => [...new Set(opcTurnos.filter((x) => !local || x.local === local).map((x) => x.setor))].filter(Boolean).sort(), [opcTurnos, local]);
  const turnosOpc = opcTurnos.filter((x) => (!local || x.local === local) && (!setor || x.setor === setor));
  const chaveTurno = (x) => `${x.local}|${x.hora}|${x.setor}`;
  const escolherTurno = (v) => {
    if (!v) { setHora(null); return; }
    const [l, h, s2] = v.split("|"); setLocal(l); setSetor(s2); setHora(h);
  };
  const filtrosAtivos = local || setor || hora;
  const limparFiltros = () => { setLocal(null); setSetor(null); setHora(null); };
  const seriesGraf = agrupar === "local"
    ? (local ? [local] : locais)
    : [...new Set((d?.por_mes_setor || []).map((x) => x.setor))].sort((a, b) =>
        (d.por_mes_setor.filter((x) => x.setor === b).reduce((s2, x) => s2 + x.participacoes, 0)) -
        (d.por_mes_setor.filter((x) => x.setor === a).reduce((s2, x) => s2 + x.participacoes, 0)));
  const t = d?.totais || {};
  const pctVazias = t.aulas ? Math.round((t.aulas_sem_participantes / t.aulas) * 100) : 0;
  const motivMedia = t.motivacao_media != null ? Number(t.motivacao_media).toFixed(1).replace(".", ",") : "—";

  const turnos = useMemo(() => {
    const arr = (d?.por_turno || []).slice();
    if (ordemSetor === "hora") arr.sort((a, b) => (a.local || "").localeCompare(b.local || "") || (a.hora || "").localeCompare(b.hora || ""));
    else arr.sort((a, b) => b[ordemSetor] - a[ordemSetor]);
    return arr;
  }, [d, ordemSetor]);
  const maxMediaTurno = Math.max(1, ...turnos.map((x) => x.media || 0));
  const maxProf = Math.max(1, ...(d?.por_professor || []).map((x) => x.aulas));

  return (
    <div style={st.wrap}>
      <div style={st.head}>
        <div>
          <h1 style={st.h1}>Presenças · {cliente}</h1>
          <div style={st.sub}>
            {titulo || "Ginástica laboral"} · {t.primeira ? `${dataLbl(t.primeira)} a ${dataLbl(t.ultima)}` : "sem registos no período"}
            {d?.atualizado_em && ` · dados atualizados ${new Date(d.atualizado_em).toLocaleString("pt-PT")}`}
          </div>
        </div>
        <div style={st.filtros}>
          {[["3m", "3 meses"], ["12m", "12 meses"], ["ano", "Este ano"], ["tudo", "Tudo"]].map(([k, l]) => (
            <button key={k} style={st.chip(periodo === k)} onClick={() => aplicarPeriodo(k)}>{l}</button>
          ))}
          <input type="date" style={st.input} value={de} onChange={(e) => { setPeriodo("custom"); setDe(e.target.value); }} />
          <input type="date" style={st.input} value={ate} onChange={(e) => { setPeriodo("custom"); setAte(e.target.value); }} />
          <select style={st.input} value={local || ""} onChange={(e) => { setLocal(e.target.value || null); setHora(null); }}>
            <option value="">Todos os locais</option>
            {locais.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select style={st.input} value={setor || ""} onChange={(e) => { setSetor(e.target.value || null); setHora(null); }}>
            <option value="">Todos os setores</option>
            {setoresOpc.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select style={st.input} value={hora ? `${local}|${hora}|${setor}` : ""} onChange={(e) => escolherTurno(e.target.value)}>
            <option value="">Todos os turnos</option>
            {turnosOpc.map((x) => <option key={chaveTurno(x)} value={chaveTurno(x)}>{x.hora} · {x.setor} · {x.local}</option>)}
          </select>
          {filtrosAtivos && <button style={st.chip(false)} onClick={limparFiltros}>✕ Limpar filtros</button>}
          {interno && <button style={{ ...st.btn, opacity: aSincronizar ? 0.6 : 1 }} disabled={aSincronizar} onClick={sincronizar}>
            {aSincronizar ? "A sincronizar…" : "↻ Atualizar do Jotform"}</button>}
          {aoSair && <button style={st.chip(false)} onClick={aoSair}>Sair</button>}
        </div>
      </div>

      {erro && <div style={{ ...st.card, borderColor: C.rosa, color: C.rosa, fontSize: 12 }}>Erro: {erro}</div>}
      {aCarregar && !d && <div style={{ fontSize: 13, color: C.cinza }}>A carregar…</div>}

      {d && (
        <div style={{ opacity: aCarregar ? 0.5 : 1, transition: "opacity .2s" }}>
          <div style={st.grid}>
            <Kpi v={fmt(t.aulas)} l="Aulas realizadas" />
            <Kpi v={fmt(t.participacoes)} l="Participações (total de presenças)" />
            <Kpi v={fmt(t.media_por_aula)} l="Média de participantes por aula" cor={C.azulEsc} />
            <Kpi v={`${pctVazias}%`} l={`Aulas sem participantes (${fmt(t.aulas_sem_participantes)})`} cor={pctVazias > 10 ? C.rosa : C.roxoEsc} />
            <Kpi v={`${motivMedia} / 5`} l="Motivação média do grupo" cor={C.roxoEsc} />
          </div>

          <div style={st.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              <h2 style={{ ...st.h2, margin: 0 }}>Evolução mensal das participações
                {filtrosAtivos && <span style={{ fontSize: 11, color: C.cinza, fontWeight: 400 }}> · {[local, setor, hora].filter(Boolean).join(" · ")}</span>}
              </h2>
              <div style={st.filtros}>
                <span style={{ fontSize: 11, color: C.cinza }}>Ver por:</span>
                {[["local", "Local"], ["setor", "Setor"]].map(([k, l]) => (
                  <button key={k} style={st.chip(agrupar === k)} onClick={() => setAgrupar(k)}>{l}</button>
                ))}
              </div>
            </div>
            <EvolucaoMensal dados={agrupar === "local" ? d.por_mes_local || [] : d.por_mes_setor || []} locais={seriesGraf} chave={agrupar} />
          </div>

          <div style={st.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <h2 style={{ ...st.h2, margin: 0 }}>Adesão por turno e setor</h2>
                <div style={{ fontSize: 11, color: C.cinza, marginTop: 3 }}>Clique num turno para ver a evolução só desse turno.</div>
              </div>
              <div style={st.filtros}>
                <span style={{ fontSize: 11, color: C.cinza }}>Ordenar:</span>
                {[["participacoes", "Participações"], ["media", "Média"], ["hora", "Local/hora"]].map(([k, l]) => (
                  <button key={k} style={st.chip(ordemSetor === k)} onClick={() => setOrdemSetor(k)}>{l}</button>
                ))}
              </div>
            </div>
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table style={{ ...st.tabela, minWidth: 520 }}>
                <thead><tr>
                  <th style={st.th}>Local</th><th style={st.th}>Turno</th><th style={st.th}>Setor</th>
                  <th style={{ ...st.th, textAlign: "right" }}>Aulas</th><th style={{ ...st.th, textAlign: "right" }}>Participações</th>
                  <th style={{ ...st.th, width: "30%" }}>Média por aula</th>
                </tr></thead>
                <tbody>
                  {turnos.map((x, i) => {
                    const sel = hora && x.hora === hora && x.setor === setor && x.local === local;
                    return (
                    <tr key={i} title={sel ? "Clique para tirar o filtro" : "Clique para filtrar por este turno"}
                      style={{ cursor: x.hora ? "pointer" : "default", ...st.zebra(i), ...(sel ? { background: C.azulClaro, boxShadow: `inset 4px 0 0 ${C.rosa}` } : {}) }}
                      onClick={() => { if (!x.hora) return; if (sel) setHora(null); else { setLocal(x.local); setSetor(x.setor); setHora(x.hora); } }}>
                      <td style={st.td}><span style={st.pill(...pillLocal(locais, x.local))}>{x.local}</span></td>
                      <td style={{ ...st.td, fontWeight: 800, color: C.roxo }}>{x.hora || "—"}</td>
                      <td style={st.td}>{x.setor ? <span style={st.pill("#EDE6F8", C.roxoEsc)}>{x.setor}</span> : "—"}</td>
                      <td style={{ ...st.td, textAlign: "right" }}>{fmt(x.aulas)}</td>
                      <td style={{ ...st.td, textAlign: "right", fontWeight: 700, color: C.roxo }}>{fmt(x.participacoes)}</td>
                      <td style={st.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ flex: 1, background: C.linha, borderRadius: 4, height: 10 }}>
                            <div style={{ width: `${((x.media || 0) / maxMediaTurno) * 100}%`, height: "100%", borderRadius: 4,
                              background: COR_LOCAL[Math.max(0, locais.indexOf(x.local)) % COR_LOCAL.length] }} />
                          </div>
                          <span style={{ width: 32, textAlign: "right" }}>{fmt(x.media)}</span>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {!turnos.length && <tr><td style={st.td} colSpan={6}>Sem dados.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div style={st.card}>
            <h2 style={st.h2}>Motivação do grupo (avaliação do professor)</h2>
            <MotivacaoMensal dados={d.motivacao_mes || []} medias={d.motivacao_media_mes || []} />
          </div>

          <div style={st.card}>
            <h2 style={st.h2}>Observações dos professores <span style={{ fontSize: 11, color: C.cinza, fontWeight: 400 }}>· {fmt((d.observacoes || []).length)} no período</span></h2>
            <Observacoes itens={d.observacoes || []} locais={locais} />
          </div>

          {interno && (
            <div style={st.card}>
              <h2 style={st.h2}>Registos por professor <span style={{ fontSize: 11, color: C.cinza, fontWeight: 400 }}>· só interno</span></h2>
              {(d.por_professor || []).map((p) => (
                <BarraH key={p.professor || "-"} label={p.professor || "Não indicado"} valor={p.aulas} max={maxProf} cor={C.roxoEsc}
                  extra={`${fmt(p.aulas)} aulas`} />
              ))}
            </div>
          )}

          <div style={{ fontSize: 10, color: C.cinza, textAlign: "right" }}>
            Fonte: registos de presença submetidos pelos professores após cada aula · Workwell
            {t.datas_estimadas > 0 && <><br />* {fmt(t.datas_estimadas)} aulas de maio/2025 sem data registada — usada a data do registo.</>}
          </div>
        </div>
      )}
    </div>
  );
}
